import React from 'react';

/**
 * ResponsiveImage komponent der automatisk skalerer billeder baseret på containerens størrelse.
 * Bruger images.weserv.nl (en lynhurtig, gratis, Cloudflare-backed billed-proxy) til at
 * generere optimerede WebP-billeder i forskellige bredder on-the-fly.
 * 
 * Dette sikrer perfekt skarphed uden downscaling-slør, lynhurtig indlæsning og
 * automatisk WebP-konvertering for alle eksisterende og fremtidige billeder.
 */
export default function ResponsiveImage({ src, alt, className, sizes, unoptimized, ...props }) {
  if (!src) return null;

  // Hvis det er et SVG-billede, en lokal sti, eller ikke en HTTP(S) URL, skal vi ikke behandle det
  const isSvg = src.toLowerCase().includes('.svg');
  const isHttp = src.startsWith('http://') || src.startsWith('https://');

  if (isSvg || !isHttp || unoptimized) {
    return <img src={src} alt={alt} className={className} {...props} />;
  }

  // Rens URL'en for eventuelle query-parametre (f.eks. ?t=...) for at sikre ren caching
  const cleanSrc = src.split('?')[0];

  // Funktion til at generere en optimeret URL med den ønskede bredde on-the-fly
  const getResizedUrl = (width) => {
    // Vi bruger images.weserv.nl til at skalere, konvertere til webp og optimere kvaliteten
    return `https://images.weserv.nl/?url=${encodeURIComponent(cleanSrc)}&w=${width}&output=webp&q=85`;
  };

  // Vi genererer et bredt udvalg af bredder, præcis som Drupals billedformater
  // Dette dækker alt fra små mobil-skærme til store desktop-skærme
  const widths = [320, 480, 640, 800, 1024, 1280, 1600, 1920];
  const srcSet = widths.map(w => `${getResizedUrl(w)} ${w}w`).join(', ');

  // Standard 'sizes' attribut, hvis ingen er angivet.
  // Fortæller browseren hvor stor containeren forventes at være på forskellige skærmstørrelser.
  const defaultSizes = sizes || "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 1200px";

  // Vi bruger en fornuftig mellemstørrelse (f.eks. 1200px) som standard src (fallback for ældre browsere)
  return (
    <img
      src={getResizedUrl(1200)}
      srcSet={srcSet}
      sizes={defaultSizes}
      alt={alt}
      className={className}
      loading="lazy"
      {...props}
    />
  );
}
